import { useCallback } from 'react'
import { z } from 'zod'

import getEndpointUrl from 'api/getEndpointUrl'

import createSearchParams from './createSearchParams'
import useGetOptions, { RequestOptions } from './useGetOptions'
import validateApiData, { JsonDataZodSchema } from './validateApiData'

export class ResponseError extends Error {
  response: Response

  constructor(message: string, response: Response) {
    super(message)
    this.name = 'ResponseError'
    this.response = response
  }
}

const processResponse = (response: Response, url: string) => {
  if (!response.ok) {
    throw new ResponseError(`API Request to ${url} failed`, response)
  }

  if (response.headers.get('Content-Type') === 'application/json') {
    return response.json()
  }

  // A stream which the caller can start processing before the whole response is fetched
  return response.body
}

const useFetchWithAuth = () => {
  const getOptions = useGetOptions()

  return useCallback(
    async <
      TData extends unknown,
      TResponseSchema extends JsonDataZodSchema | undefined = undefined,
    >(
      url: string,
      { params, responseSchema, timeout, ...restOptions }: RequestOptions<TResponseSchema> = {}
    ) => {
      const options = { ...restOptions }

      const urlWithParams = (() => {
        if ((!restOptions.method || restOptions.method === 'GET') && params) {
          return `${url}?${createSearchParams(params)}`
        } else {
          return url
        }
      })()

      const hydratedOptions = await getOptions(urlWithParams, options)

      let abortTimeout: NodeJS.Timeout | undefined
      if (timeout) {
        const abortController = new AbortController()
        hydratedOptions.signal = abortController.signal
        abortTimeout = setTimeout(() => {
          abortController.abort()
        }, timeout)
      }

      const response = (await fetch(getEndpointUrl(urlWithParams), hydratedOptions).then(
        async (res) => {
          if (responseSchema) {
            const data = (await processResponse(res, urlWithParams)) as unknown
            return validateApiData({ data, schema: responseSchema })
          } else {
            return processResponse(res, urlWithParams) as TData
          }
        }
      )) as Promise<TResponseSchema extends JsonDataZodSchema ? z.infer<TResponseSchema> : TData>

      clearTimeout(abortTimeout)

      return response
    },
    [getOptions]
  )
}

export default useFetchWithAuth
